import { useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, CircleHelp, Moon, Plus, Sun } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { useChurch } from '@admin/shared/contexts/ChurchContext';
import { useEntitlements } from '@admin/shared/contexts/EntitlementsContext';
import { displayTierName } from '@app/utils/tierUtils';
import { useTheme } from '@app/theme/theme';
import { useLiveNotifications } from '@app/notifications/LiveNotifications';
import { Avatar, Menu, MenuDivider, MenuItem, MenuLabel, MenuTileItem, cx, initialsOf } from '@app/components/ui';
import { useMediaQuery, BP } from '@app/hooks/useMediaQuery';
import { useTeamHeadcounts } from '@app/hooks/useTeamHeadcounts';
import { PRIMARY_TABS, groupForPath } from './nav';

// Tab width: 96px, or 80px on phones so all four tabs fit a 390px screen
// (4 × 96 plus padding overflowed and clipped "Setlists").
const TAB_W = 96;
const TAB_W_PHONE = 80;

function PrimaryNav({ className }: { className?: string }) {
  const { pathname } = useLocation();
  const group = groupForPath(pathname);
  const idx = PRIMARY_TABS.findIndex((t) => t.group === group);
  const tabW = useMediaQuery(BP.roomy) ? TAB_W : TAB_W_PHONE;
  return (
    <nav
      aria-label="Primary"
      className={cx('relative flex p-1 rounded-full border border-line bg-fill max-w-full overflow-x-auto', className)}
    >
      <span
        aria-hidden="true"
        className="absolute left-1 top-1 h-9 rounded-full bg-t1"
        style={{
          width: tabW,
          transform: `translateX(${Math.max(idx, 0) * tabW}px)`,
          opacity: idx >= 0 ? 1 : 0,
          transition: 'transform .5s var(--ease), opacity .3s ease',
        }}
      />
      {PRIMARY_TABS.map((t) => {
        const on = t.group === group;
        return (
          <Link
            key={t.path}
            to={t.path}
            aria-current={on ? 'page' : undefined}
            className={cx(
              'relative z-[1] shrink-0 h-9 rounded-full flex items-center justify-center text-[14px] font-medium',
              on ? '!text-bg' : '!text-t3 hover:!text-t1',
            )}
            style={{ width: tabW, transition: 'color .35s ease' }}
          >
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function Header({ onNewSetlist }: { onNewSetlist: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const group = groupForPath(pathname);
  const { user, logout } = useAuth();
  const { church } = useChurch();
  const { groups, selectedGroup, setSelectedGroupId } = useGroup();
  const { subscription, canUseChordAi, canUseApiKeys } = useEntitlements();
  const { isDark, toggle } = useTheme('admin');
  const { hasUnread, pendingCount } = useLiveNotifications();
  const wideNav = useMediaQuery(BP.wideNav);

  const [menu, setMenu] = useState<null | 'team' | 'create' | 'avatar'>(null);
  const teamRef = useRef<HTMLButtonElement>(null);
  const createRef = useRef<HTMLButtonElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);
  const close = () => setMenu(null);
  const at = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  const go = (path: string) => {
    close();
    navigate(path);
  };

  // Counted from membership rows with the service's rule (see useTeamHeadcounts).
  const counts = useTeamHeadcounts(user?.churchId);
  const headcount = (g: { id: string }) => {
    if (!counts) return '';
    const n = counts[g.id] ?? 0;
    return `${n} ${n === 1 ? 'person' : 'people'}`;
  };
  const teamName = selectedGroup?.name ?? (groups.length ? 'Choose team' : 'No team');
  const multiChurch = (user?.adminChurches?.length ?? 0) > 1;
  const churchName = church?.name ?? user?.adminChurches?.find((c) => c.churchId === user?.churchId)?.churchName ?? '';
  const scoped = ['home', 'roster', 'setlists'].includes(group) || pathname.startsWith('/admin/team');

  return (
    <header
      className="sticky top-0 z-40 border-b border-line bg-hdr"
      style={{ backdropFilter: 'saturate(1.4) blur(16px)', WebkitBackdropFilter: 'saturate(1.4) blur(16px)' }}
    >
      <div
        className="max-w-[1280px] mx-auto h-16 px-3.5 min-[640px]:px-5 grid items-center gap-4"
        style={{ gridTemplateColumns: 'minmax(0,1fr) auto minmax(max-content,1fr)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <NavLink
            to="/admin/dashboard"
            aria-label="Worship Planner home"
            className="flex items-center gap-2.5 !text-t1 min-w-[30px] shrink overflow-hidden"
          >
            <img src="/favicon.svg" alt="" className="w-[30px] h-[30px] min-w-[30px] rounded-[8px] block" />
            <span className="hidden min-[1100px]:inline text-[16px] font-bold tracking-[-0.01em] whitespace-nowrap">
              Worship Planner
            </span>
          </NavLink>
          <button
            ref={teamRef}
            type="button"
            aria-label={`Worship team: ${teamName}. Switch team`}
            aria-haspopup="menu"
            aria-expanded={menu === 'team'}
            onClick={() => setMenu(menu === 'team' ? null : 'team')}
            className={cx(
              'shrink-0 h-[34px] pl-3 pr-2.5 rounded-full border border-line2 bg-fill hover:bg-fill2 text-[13px] font-medium flex items-center gap-[7px] whitespace-nowrap max-w-[220px]',
              scoped ? 'text-t1' : 'text-t3',
            )}
          >
            <span className="w-[7px] h-[7px] rounded-full bg-acc shrink-0" aria-hidden="true" />
            <span className="truncate min-[1180px]:hidden">{teamName.split(' ')[0]}</span>
            <span className="truncate hidden min-[1180px]:inline">{teamName}</span>
            <ChevronDown size={13} strokeWidth={2.2} className="text-t4 shrink-0" aria-hidden="true" />
          </button>
        </div>

        {wideNav ? <PrimaryNav /> : <span />}

        <div className="col-start-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={toggle}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-9 h-9 rounded-full bg-fill2 text-t3 hover:text-t1 flex items-center justify-center"
          >
            {isDark ? <Sun size={17} strokeWidth={1.8} aria-hidden="true" /> : <Moon size={17} strokeWidth={1.8} aria-hidden="true" />}
          </button>
          <button
            ref={createRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={menu === 'create'}
            aria-label="Create"
            onClick={() => setMenu(menu === 'create' ? null : 'create')}
            className="wp-press h-[38px] pl-3 pr-3 min-[640px]:pr-4 rounded-full bg-acc hover:bg-acc-h text-white text-[14px] font-semibold flex items-center gap-1.5"
          >
            <Plus size={17} strokeWidth={2.2} aria-hidden="true" />
            <span className="hidden min-[640px]:inline">Create</span>
          </button>
          <NavLink
            to="/admin/notifications"
            aria-label={hasUnread ? 'Notifications, unread' : 'Notifications'}
            className={cx(
              'relative w-9 h-9 rounded-full !text-t3 hover:!text-t1 flex items-center justify-center',
              group === 'none' ? 'bg-line2' : 'bg-fill2',
            )}
          >
            <Bell size={17} strokeWidth={1.8} aria-hidden="true" />
            {hasUnread && (
              <span
                aria-hidden="true"
                className="absolute top-[7px] right-2 w-2 h-2 rounded-full bg-acc"
                style={{ boxShadow: '0 0 0 2px var(--bg)' }}
              />
            )}
          </NavLink>
          <NavLink
            to="/admin/support"
            aria-label="Help"
            className={cx(
              'w-9 h-9 rounded-full !text-t3 hover:!text-t1 flex items-center justify-center',
              group === 'help' ? 'bg-line2' : 'bg-fill2',
            )}
          >
            <CircleHelp size={18} strokeWidth={1.8} aria-hidden="true" />
          </NavLink>
          <button
            ref={avatarRef}
            type="button"
            aria-label="Account"
            aria-haspopup="menu"
            aria-expanded={menu === 'avatar'}
            onClick={() => setMenu(menu === 'avatar' ? null : 'avatar')}
            className={cx(
              'w-9 h-9 rounded-full text-[12px] font-bold',
              group === 'account' ? 'bg-t1 text-bg' : 'bg-avatar text-t2',
            )}
          >
            {initialsOf(user?.username)}
          </button>
        </div>
      </div>

      {!wideNav && (
        <div className="flex justify-center px-4 pb-3">
          <PrimaryNav />
        </div>
      )}

      <Menu open={menu === 'team'} onClose={close} anchorRef={teamRef} label="Worship teams" width={320}>
        <MenuLabel>Worship teams</MenuLabel>
        {groups.map((g) => (
          <MenuTileItem
            key={g.id}
            selected={g.id === selectedGroup?.id}
            onSelect={() => {
              setSelectedGroupId(g.id);
              close();
            }}
            title={g.name}
            sub={headcount(g)}
            trailing={g.id === selectedGroup?.id ? <span className="text-acc font-bold" aria-hidden="true">✓</span> : null}
          />
        ))}
        <MenuDivider />
        <MenuTileItem onSelect={() => go('/admin/team?new=1')} glyph="+" tone="accent" title="New worship team" />
        <MenuItem onSelect={() => go('/admin/team')} className="!text-t3 !text-[13px]">
          Manage teams
        </MenuItem>
      </Menu>

      <Menu open={menu === 'create'} onClose={close} anchorRef={createRef} label="Create" width={320} align="end">
        <MenuTileItem onSelect={() => go('/admin/roster?plan=new')} glyph="+" tone="accent" title="New service plan" sub="Date, team and setlist" />
        <MenuTileItem onSelect={() => go('/admin/songs?add=1')} glyph="♪" title="Add song" sub="To your library" />
        <MenuTileItem
          onSelect={() => {
            close();
            onNewSetlist();
          }}
          glyph="≡"
          title="New setlist"
          sub="For an upcoming service"
        />
        <MenuTileItem onSelect={() => go('/admin/invite')} glyph="#" title="Invite member" sub="Generate an invite code" />
        <MenuTileItem
          onSelect={() => go(canUseChordAi ? '/admin/chord-library?upload=1' : '/admin/subscription')}
          glyph="↑"
          tone="amber"
          title="Upload to Song Studio"
          sub="Rehearsal recording from your computer"
        />
      </Menu>

      <Menu open={menu === 'avatar'} onClose={close} anchorRef={avatarRef} label="Account" width={280} align="end">
        <div className="flex items-center gap-3 px-2.5 pt-2.5 pb-3.5 border-b border-line mb-1.5">
          <Avatar name={user?.username} size={40} />
          <span className="min-w-0">
            <span className="block text-[14.5px] font-semibold truncate">{user?.username}</span>
            <span className="block text-[12.5px] text-t4 truncate">
              {user?.userRole || 'Admin'}
              {churchName ? ` · ${churchName}` : ''}
            </span>
          </span>
        </div>
        <MenuItem current={at('/admin/profile')} onSelect={() => go('/admin/profile')}>
          Profile
        </MenuItem>
        <MenuItem current={at('/admin/settings')} onSelect={() => go('/admin/settings')}>
          Church settings
        </MenuItem>
        <MenuItem current={group === 'people'} onSelect={() => go('/admin/team')} hint={pendingCount ? `${pendingCount} request${pendingCount === 1 ? '' : 's'}` : undefined}>
          Teams and people
        </MenuItem>
        <MenuItem current={group === 'billing'} onSelect={() => go('/admin/subscription')} hint={subscription ? displayTierName(subscription.tier) : undefined}>
          Plan and billing
        </MenuItem>
        <MenuItem current={at('/admin/notifications')} onSelect={() => go('/admin/notifications')}>
          Notifications
        </MenuItem>
        <MenuItem current={group === 'help'} onSelect={() => go('/admin/tips')}>
          Tips and support
        </MenuItem>
        {canUseApiKeys && (
          <MenuItem current={at('/admin/developer-settings')} onSelect={() => go('/admin/developer-settings')}>
            Developer
          </MenuItem>
        )}
        {multiChurch && <MenuItem onSelect={() => go('/select-church')}>Switch church</MenuItem>}
        <MenuItem
          danger
          onSelect={() => {
            close();
            void logout();
          }}
        >
          Log out
        </MenuItem>
      </Menu>
    </header>
  );
}
